import { ServerMessageCode } from '@/constant';
import { ApiResponseError } from '@/services';
import { setApiAuthorization } from '@/services/api/api.util';
import { refreshToken } from '@/services/api/auth.api';
import { AuthActions, AuthSelectors } from '@/stores';
import { NOOP, showAlert } from '@/utils';
import { isMobile } from '@/utils/mobile.util';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { jwtDecode } from 'jwt-decode';

import { useCountdown } from './useCountdown.hook';
import useSessionStorage from './useSessionStorage';

const SESSION_DURATION_MS = 60 * 60 * 1000;

const WARNING_REMAINING_MS = 60 * 1000;

const POPUP1_COUNTDOWN_MS = 60 * 1000;

const SESSION_END_AT_KEY = 'fw_session_end_at';

export interface UseSessionTimerReturn {
	/** Display string e.g. "60:00" */
	timer: string;
	/** Whether the countdown is currently running */
	isActive: boolean;
	/** Call API to extend session (BE resets token). On BE error (e.g. 8 times exceeded) shows alert. */
	onExtend: () => void;
	/** True while extend API is in progress */
	extendLoading: boolean;
	/** Number of successful extends this session (for display) */
	extendCount: number;
}

interface ITokenJwtDecode {
	exp?: number;
	dailyExtendCount?: number;
}

/**
 * Session timer after login: 60 min countdown, auto logout on expire.
 * Extend calls BE API; BE returns error after 8 extends, we show alert on error.
 */
export const useSessionTimer = (): UseSessionTimerReturn => {
	const dispatch = useDispatch();
	const isLogged = useSelector(AuthSelectors.selectLogged);
	const accessToken = useSelector(AuthSelectors.selectAccessToken);
	const [sessionEndAt, setSessionEndAt] = useSessionStorage<number | null>(
		SESSION_END_AT_KEY,
		null
	);
	const [extendCount, setExtendCount] = useState(0);
	const [extendLoading, setExtendLoading] = useState(false);
	const hasTriggeredLogoutRef = useRef(false);
	const hasStartedSessionRef = useRef(false);
	const hasShownWarningRef = useRef(false);
	const warningTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const onExtendRef = useRef<() => Promise<void>>(async () => {});
	const hasExtendLimitExceededRef = useRef(false);

	const { timer, handleStart, isCountdown, minutes, seconds } =
		useCountdown(SESSION_DURATION_MS);

	// Start countdown once when user becomes logged in; restore from sessionStorage on F5
	useEffect(() => {
		if (isLogged) {
			hasTriggeredLogoutRef.current = false;
			hasShownWarningRef.current = false;
			hasExtendLimitExceededRef.current = false;
			if (!hasStartedSessionRef.current) {
				hasStartedSessionRef.current = true;
				const now = Date.now();

				// If we have a persisted end time in sessionStorage, prefer it (e.g. after F5).
				let expMs: number | null = null;
				if (accessToken) {
					try {
						const decoded = jwtDecode<ITokenJwtDecode>(accessToken);
						if (decoded.exp) {
							expMs = decoded.exp * 1000;
						}

						if (decoded?.dailyExtendCount) {
							setExtendCount(decoded.dailyExtendCount);
						}
					} catch {
						// Ignore decode errors and fall back to fixed duration.
					}
				}

				if (sessionEndAt != null && sessionEndAt > now) {
					handleStart(sessionEndAt - now);
				} else {
					if (expMs && expMs > now) {
						const remainingMs = expMs - now;
						handleStart(remainingMs);
						setSessionEndAt(expMs);
					} else {
						// Fallback: use fixed session duration (60 min).
						handleStart();
						setSessionEndAt(now + SESSION_DURATION_MS);
					}
				}
			}
		} else {
			hasStartedSessionRef.current = false;
			setSessionEndAt(null);
			if (warningTimeoutRef.current) {
				clearTimeout(warningTimeoutRef.current);
				warningTimeoutRef.current = null;
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- only start on isLogged change
	}, [isLogged]);

	// Mobile: show Popup (1) when remaining time <= WARNING_REMAINING_MS; skip if extend limit (8x) already exceeded
	useEffect(() => {
		if (!isMobile || !isLogged || !isCountdown || hasShownWarningRef.current)
			return;
		if (hasExtendLimitExceededRef.current) return;

		const remainingMs = (minutes * 60 + seconds) * 1000;
		if (remainingMs > WARNING_REMAINING_MS || remainingMs <= 0) return;

		// Countdown inside popup should match the actual remaining time (capped by POPUP1_COUNTDOWN_MS)
		const popupCountdownMs = Math.min(remainingMs, POPUP1_COUNTDOWN_MS);

		hasShownWarningRef.current = true;

		const doLogout = () => {
			hasTriggeredLogoutRef.current = true;
			setSessionEndAt(null);
			dispatch(AuthActions.logoutRequest({ isCallApi: true }));
		};

		// After countdown without action: show Popup (2) and logout (replaces current alert)
		warningTimeoutRef.current = setTimeout(() => {
			warningTimeoutRef.current = null;
			doLogout();
			showAlert('automatic-logout-notice', NOOP, undefined, {
				message: 'automatic-logout-notice-message-2',
				hideDivide: true,
			});
		}, popupCountdownMs);

		// Popup (1)
		showAlert(
			'automatic-logout-notice',
			async () => {
				if (warningTimeoutRef.current) {
					clearTimeout(warningTimeoutRef.current);
					warningTimeoutRef.current = null;
				}
				await onExtendRef.current();
				// Only allow Popup (1) again after extend succeeds (timer reset); avoids re-show during async
				hasShownWarningRef.current = false;
			},
			() => {
				if (warningTimeoutRef.current) {
					clearTimeout(warningTimeoutRef.current);
					warningTimeoutRef.current = null;
				}
				doLogout();
			},
			{
				message: 'automatic-logout-notice-message-countdown',
				countdownMs: popupCountdownMs,
				cancelText: 'logout',
				confirmText: 'extend-login-session',
				hideDivide: true,
			}
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps -- only when 1 min left
	}, [isLogged, isCountdown, minutes, seconds]);

	// Auto logout when main countdown reaches 0
	useEffect(() => {
		if (!isLogged || !isCountdown) return;
		if (minutes === 0 && seconds === 0) {
			if (!hasTriggeredLogoutRef.current) {
				hasTriggeredLogoutRef.current = true;
				setSessionEndAt(null);
				dispatch(AuthActions.logoutRequest({ isCallApi: true }));
			}
		}
	}, [isLogged, isCountdown, minutes, seconds, dispatch]);

	const onExtend = useCallback(async () => {
		if (extendLoading) return;
		setExtendLoading(true);
		try {
			const res = await refreshToken();
			const newToken = res?.data;
			if (newToken) {
				dispatch(AuthActions.renewAccessToken({ accessToken: newToken }));
				setApiAuthorization(newToken);

				const now = Date.now();
				let expMs: number | null = null;
				let dailyExtendCount: number | null = null;

				try {
					const decoded = jwtDecode<ITokenJwtDecode>(newToken);
					if (decoded.exp) {
						expMs = decoded.exp * 1000;
					}
					if (decoded?.dailyExtendCount) {
						dailyExtendCount = decoded.dailyExtendCount;
					}
				} catch {
					// Ignore decode errors and fall back to fixed duration.
				}

				if (expMs && expMs > now) {
					const remainingMs = expMs - now;
					handleStart(remainingMs);
					setSessionEndAt(expMs);
				} else {
					handleStart();
					setSessionEndAt(now + SESSION_DURATION_MS);
				}

				if (dailyExtendCount) {
					setExtendCount(dailyExtendCount);

					showAlert('extend-login-session-2', NOOP, undefined, {
						message: 'your-login-session-has-been-extended',
						messageOptions: {
							total: 8,
							dailyExtendCount,
						},
						hideDivide: true,
					});
				}
			}
		} catch (err) {
			// BE returns error when extend limit exceeded (e.g. 8 times); interceptor skips alert for this URL
			// Do not reset timer on error
			console.error(err);
			if (
				(err as ApiResponseError)?.response?.data?.messageCode ===
				ServerMessageCode.SYSTEM_SESSION_EXTEND_LIMIT_EXCEEDED
			) {
				hasExtendLimitExceededRef.current = true;
				showAlert(
					'SYSTEM_SESSION_EXTEND_LIMIT_EXCEEDED_TITLE',
					NOOP,
					undefined,
					{
						message: 'SYSTEM_SESSION_EXTEND_LIMIT_EXCEEDED',
						hideDivide: true,
					}
				);
			}
		} finally {
			setExtendLoading(false);
		}
	}, [extendLoading, dispatch, handleStart, isMobile]);

	onExtendRef.current = onExtend;

	return {
		timer,
		isActive: isCountdown,
		onExtend,
		extendLoading,
		extendCount,
	};
};
